/*
StateNotifiction is intended to show a status of a process. There are 3 statuses: PENDING, REJECTED and FULFILLED.

A basic example of usage:

```
const [state, setState] = useState<State>({ status: FULFILLED });
const [data, setData] = useState();

const load = () =>
  setState({ status: PENDING });
    fetch(...)
      .then((v) => {
        setData(v);
        setState({ status: FULFILLED });
      })
      .catch((v) => setState({ status: REJECTED, error: v }));
  }

useEffect(load, []);

return (
  <>
    <StateNotification state={state}>{data}</StateNotification>
    <button onClick={load}>reload</button>
  </>
);
```

While data is being loaded then a circular spinner is shown. If the data has been successfully loaded then the data is shown.
If the loading has been completed with an error then this error is shown in a Chip.
If the data was loaded and the "reload" button is pressed then a new loading starts and a spinner is shown instead of the data.

If it is necessary not to hide the data on loading then the component could be rewritten like this:

```
  <>
    {data}
    <StateNotification state={state} progress={LINEAR} />
    <button onClick={load}>reload</button>
  </>
```

In this example the loading is shown as a full width animated line. A linear loader looks better
when it is located below the content but a circular one could be used too.

In the previous example the button can be pressed before the loading is completed. It can be fixed like this:

```
<button disabled={state.status === PENDING} onClick={load}>reload</button>
```

or OVERLAY can be used:

```
  <StateNotification state={state} progress={OVERLAY}>
    {data}
    <button onClick={load}>reload</button>
  </StateNotification>
```

In this case when the `state.status` is PENDING then the children of StateNotification are covered
with translucent `div` with a circular spinner in the middle.
If OVERLAY is used then an error is not shown automatically. It can be shown like this:

```
  <StateNotification state={state} progress={OVERLAY}>
    {data}
    <button onClick={load}>reload</button>
    {state.status === REJECTED && <StateNotification state={state} />}
  </StateNotification>
```

or another indication can be used, for example a popup in the bottom of the page:

```
import useAlerts from 'admin-mojo/widgets/Alerts';
const alert = useAlerts();

const load = () =>
  setState({ status: PENDING });
    fetch(...)
      .then((v) => setData(v))
      .catch((v) => alert(v))
      .finally(() => setState({ status: FULFILLED }));
  }
```
 */

import * as React from 'react';
import { FC, CSSProperties, ReactElement, ReactNode } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import { Error } from './ui';

export const PENDING = 'pending';
export const REJECTED = 'rejected';
export const FULFILLED = 'fulfilled';
export type Status = 'pending' | 'rejected' | 'fulfilled';

export const LINEAR = 'linear';
export const CIRCULAR = 'circular';
export const OVERLAY = 'overlay';
export type Progress = 'linear' | 'circular' | 'overlay';

export type SuccessfulState = {
  status: typeof FULFILLED;
};

export type PendingState = {
  status: typeof PENDING;
};

export type RejectedState = {
  status: typeof REJECTED;
  error: string;
};

export type State = SuccessfulState | PendingState | RejectedState;

const LINEAR_HEIGHT = '5px';
const CIRCULAR_HEIGHT = '57px';

function defaultTexts(status: Status) {
  switch (status) {
    case REJECTED:
      return 'Sorry, something went wrong';
    default:
      return null;
  }
}

const Centralized: FC<{ children: ReactNode }> = ({ children }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      height: '100%',
    }}
  >
    {children}
  </div>
);

function wrapperClasses({ state, className = '' }: { state: State; className?: string }) {
  return ['state-notifications', state.status, className].join(' ');
}

const AlignedToProgressHeight: FC<{ progress: Progress; children?: ReactNode }> = ({ progress, children }) => (
  <div style={{ height: progress === LINEAR ? LINEAR_HEIGHT : CIRCULAR_HEIGHT }}>{children}</div>
);

const ProgressComponent = ({ progress }: { progress: Progress }) => {
  if (progress === CIRCULAR) {
    return (
      <Centralized>
        <AlignedToProgressHeight progress={CIRCULAR}>
          <CircularProgress variant="indeterminate" />
        </AlignedToProgressHeight>
      </Centralized>
    );
  }
  return <LinearProgress variant="indeterminate" />;
};

const Content: FC<{ progress: Progress; children: ReactNode }> = ({ progress, children }) =>
  children ? <>{children}</> : <AlignedToProgressHeight progress={progress} />;

const Overlay: FC = () => (
  <>
    <div
      style={{
        position: 'absolute',
        width: '100%',
        height: '100%',
        zIndex: 999,
        opacity: '.5',
        background: 'white',
      }}
    />
    <div
      style={{
        position: 'absolute',
        width: '100%',
        height: '100%',
        zIndex: 9999,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: CIRCULAR_HEIGHT,
      }}
    >
      <CircularProgress variant="indeterminate" />
    </div>
  </>
);

// Errors shown only if no children provided
const ContentWithOverlay: FC<{ state: State; children: ReactNode }> = ({ state, children }) => (
  <>
    {state.status === PENDING ? <Overlay /> : null}
    {state.status === REJECTED && !children ? (
      <Centralized>
        <Error text={state.error || defaultTexts(REJECTED)} />
      </Centralized>
    ) : null}
    {children}
  </>
);

const StateComponent: React.FC<{
  state: State;
  progress: Progress;
  innerWrapper?: (content: ReactElement) => ReactElement;
  children: ReactNode;
}> = ({ state, progress, innerWrapper, children }) => {
  const wrapper = innerWrapper || ((v: ReactElement) => v);

  if (progress === OVERLAY) return <ContentWithOverlay state={state}>{children}</ContentWithOverlay>;

  switch (state.status) {
    case PENDING:
      return wrapper(<ProgressComponent progress={progress} />);
    case REJECTED:
      return wrapper(
        <Centralized>
          <Error text={state.error || defaultTexts(REJECTED)} />
        </Centralized>
      );
    default:
      return <Content progress={progress}>{children}</Content>;
  }
};

type Wrapper = FC<{ style: CSSProperties; className: string }>;

type StateNotificationProps = {
  state: State;
  progress?: Progress;
  className?: string;
  outerWrapper?: Wrapper; // used to wrap everything in each state
  innerWrapper?: (content: ReactElement) => ReactElement; // used to wrap error message or spinner
  children?: ReactNode;
};

const StateNotification: React.FC<StateNotificationProps> = ({
  state,
  progress = CIRCULAR,
  className,
  outerWrapper,
  innerWrapper,
  children,
}) => {
  const OuterWrapper = outerWrapper || 'div';

  return (
    <OuterWrapper style={{ width: '100%', position: 'relative' }} className={wrapperClasses({ state, className })}>
      <StateComponent {...{ state, progress, innerWrapper }}>{children}</StateComponent>
    </OuterWrapper>
  );
};

export default StateNotification;
